import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { pageContext, replaceBaseUrl, onReplaceLinkClick } from 'ctm-util-functions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/pro-regular-svg-icons';
import { faSquare } from '@fortawesome/pro-light-svg-icons';
import { faInfo, faCheckSquare } from '@fortawesome/pro-solid-svg-icons';
import { connect } from 'react-redux';
import { ProviderLogo } from '../products/products/common/provider_logo';
import { MechaButton } from '../../../ctm-components/components/mecha_components/mecha_button/mecha_button';
import { ExcessDetails } from './excess_details';
import { ProductDetails } from './product_details';
import { destructNumber } from '../products/products/common/number_formatter';
import { ModalContainer } from '../../../ctm-components/components/modal/modal_container';
import { TextTemplate } from '../../../ctm-components/components/text_template/text_template';
import { retrieveDisclaimer } from './utils';

const backgroundPrimarySubtle = '#F4F8FD';
const backgroundPrimary = '#E9F2FB';
const blue300 = '#B4D4F8';
const blue400 = '#0F58AB';
const contentPrimary = '#001464';
const contentSecondary = '#001443';
const contentTertiary = '#404756';
const linkPrimary = '#0F58AB';

const Container = styled.div`
    border-radius: 16px;
    border: 1px solid ${blue300};
    background: ${backgroundPrimarySubtle};
    box-shadow: 0px 7px 5px 0px rgba(0, 0, 0, 0.03);
    max-width: 752px;
    display: flex;
    flex-direction: column;
    width: 100%;
    ${({ summary }) => (summary
        ? `
      background: ${backgroundPrimarySubtle};
  `
        : `
      background: #FFFFFF;
`)}
`;

const SummaryContainer = styled.div`
    display: flex;
    align-items: flex-start;
    align-self: stretch;
    @media only screen and (max-width: 600px) {
        flex-direction: column;
    }
`;

const SummaryContentContainer = styled.div`
    display: flex;
    padding: 16px 24px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    flex: 1 0 0;
    align-self: stretch;
    background: #fff;
    border-top-left-radius: 16px;
    @media only screen and (max-width: 600px) {
        border-top-right-radius: 16px;
    }
`;

const SummaryContentTitleContainer = styled.div`
    display: flex;
    padding-bottom: 12px;
    align-items: flex-start;
    align-self: stretch;
    border-bottom: 1px solid ${blue300};
    @media only screen and (max-width: 600px) {
        justify-content: center;
    }
`;

const SummaryContentTitle = styled.label`
    color: ${contentPrimary};
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 165%;
`;

const SummaryContentHighlightContainer = styled.div`
    display: flex;
    padding: 0px 0px 12px 0px;
    align-items: baseline;
    gap: 8px;
    align-self: stretch;
    border-bottom: 1px solid ${blue300};
`;

const SummaryContentHighlight = styled.label`
    color: ${contentTertiary};
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 170%;
    flex: 1 0 0;
`;

const SummaryContentHighlightMoreInfo = styled.label`
    color: #1c3e94;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 170%;
    flex: 1 0 0;
    cursor: pointer;
`;

const SummaryContentHighlightIcon = styled.img`
    position: relative;
    top: 3px;
`;

const SummaryContentQuoteContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    padding-bottom: 16px;
    padding-top: 5px;
    @media only screen and (max-width: 600px) {
        padding-bottom: 0px;
    }
`;

const SummaryContentQuoteExcessLabel = styled.label`
    color: ${contentPrimary};
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 170%;
`;

const SummaryContentQuoteExcessNumber = styled(SummaryContentQuoteExcessLabel)`
    font-weight: 700;
`;

const SummaryContentQuoteRef = styled(SummaryContentQuoteExcessLabel)`
    color: ${contentTertiary};
`;

const ImageContainer = styled.div`
    display: flex;
    width: 267px;
    padding: 24px 40px 32px 40px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 6px;
    align-self: stretch;
    background: ${backgroundPrimary};
    border-top-right-radius: 16px;
    @media only screen and (max-width: 600px) {
        width: 100%;
        border-top-right-radius: 0px;
        padding: 16px 16px 16px 16px;
    }
`;

const ImageTCText = styled.label`
    color: ${contentTertiary};
    font-size: 10px;
    font-style: normal;
    font-weight: 400;
    line-height: 121%;
`;

const ImagePrimiumContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: flex-start;
    margin-top: -4px;
`;

const PriceContainer = styled.div`
    display: flex;
    align-items: baseline;
`;

const PriceDollarSign = styled.sup`
    font-size: 20px;
    font-weight: 400;
    line-height: 140%;
    padding-top: 6px;
`;

const PriceWhole = styled.label`
    color: ${contentPrimary};
    text-align: center;
    font-size: 36px;
    font-style: normal;
    font-weight: 800;
    line-height: 140%;
`;

const PriceDecimal = styled.label`
    color: ${contentPrimary};
    text-align: center;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 160%;
`;

const Frequency = styled.label`
    color: ${contentPrimary};
    text-align: center;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 180%;
    margin-top: -20px;
`;

const PolicyDetailToggle = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  cursor: pointer;
  background: ${backgroundPrimarySubtle};
  border-bottom-right-radius: ${(props) => (props.isExpand ? '0' : '16px')};
  border-bottom-left-radius: ${(props) => (props.isExpand ? '0' : '16px')};
  @media only screen and (max-width: 600px) {
    justify-content: center;
  }
`;

const PolicyDetailToggleText = styled.label`
    color: ${contentSecondary};
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 121%;
`;

const PolicyDetailContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    align-self: stretch;
    border-bottom-radius: 16px;
`;

const PolicyDetailFlexBox = styled.div`
  display: flex;
  align-self: stretch;
  border-bottom: 1px solid ${blue300};
  gap: 32px;
  align-items: flex-start;
  padding: 8px 8px 0px 16px;
  justify-content: space-around;
  background: ${backgroundPrimarySubtle};
  @media only screen and (max-width: 600px) {
    flex-direction: column;
    padding: 8px 16px 0px 16px;
    gap: 0px;
    border-bottom: 0px;
  }
`;

const PolicyDetailLeftColumn = styled.div`
    display: flex;
    flex-direction: column;
    padding: 8px 0px 0px 0px;
    align-items: flex-start;
`;

const PolicyDetailRightColumn = styled(PolicyDetailLeftColumn)`
    padding: 8px 8px 0px 16px;
    width: 250px;
    @media only screen and (max-width: 600px) {
        flex-direction: column;
        padding: 0;
        width: 100%;
        margin-top: -8px;
    }
`;

const PolicyDetailDisclaimerContainer = styled.div`
  padding: 16px 8px 16px 16px;
  background: ${backgroundPrimarySubtle};
  border-bottom-right-radius: 16px;
  border-bottom-left-radius: 16px;
  @media only screen and (max-width: 600px) {
    padding-top: 0px;
  }
`;

const ProductDisclosureContainer = styled.div`
    padding: 8px 0px 16px 0px;
`;

const ProductDisclosureText = styled.label`
    color: ${contentSecondary};
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 170%;
    align-self: stretch;
`;

const ProductDisclosureLinkContainer = styled.a`
    color: ${linkPrimary};
    leading-trim: both;
    text-edge: cap;
    font-size: 11px;
    font-style: normal;
    font-weight: 900;
    line-height: 135%;
    text-decoration-line: underline;
    display: block;
    padding-top: 4px;
    &:hover {
        cursor: pointer;
    }
`;

const ProductDisclosureLinkText = styled.label`
    color: ${linkPrimary};
    leading-trim: both;
    text-edge: cap;
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 135%;
    text-decoration-line: underline;
`;

const UpdateContainer = styled.div`
    color: ${backgroundPrimary};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 6px;
    align-self: stretch;
`;

const UpdateTitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 20px;
    padding: 24px 40px;
    width: 100%;
    justify-content: center;
    background: ${backgroundPrimary};
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    @media only screen and (max-width: 600px) {
        flex-direction: column;
        padding: 16px 32px;
    }
`;

const UpdateTitle = styled.label`
    color: ${contentPrimary};
    text-align: center;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 136%;
`;
const UpdateInfoIconContainer = styled.div`
    display: flex;
    width: 27px;
    height: 27px;
    padding: 4px 6px 6px 6px;
    justify-content: center;
    align-items: center;
    border-radius: 32px;
    background: ${blue400};
    @media only screen and (max-width: 600px) {
        padding: 6px 8px 8px 8px;
        border-radius: 42.667px;
        width: 32px;
        height: 32px;
    }
`;

const UpdateContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    align-self: stretch;
`;

const UpdateContentHeaderContainer = styled.div`
    display: flex;
    align-items: flex-start;
`;

const UpdateContentHeaderText = styled.label`
    color: ${contentTertiary};
    text-align: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 170%;
`;

const UpdateConfirmContainer = styled.div`
    display: flex;
    padding: 4px 0px 8px 0px;
    align-items: flex-start;
    gap: 8px;
    @media only screen and (max-width: 600px) {
        align-items: flex-start;
    }
`;

const UpdateConfirmText = styled.label`
    color: ${contentTertiary};
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 175%;
`;

const UpdateBodyContainer = styled.div`
    background: #ffffff;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 30px 64px;
    gap: 8px;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
    width: 100%;
    @media only screen and (max-width: 600px) {
        padding: 30px 35px;
    }
`;

const UpdateCheckBoxContainer = styled.div`
    padding-top: 2px;
`;

const DiscountOfferTermsHeading = styled.h3`
    padding-top: 2rem;
    padding-left: 2rem;
    color: #333333;
`;

const DiscountOfferTermsContainer = styled.div`
    display: flex;
    align-items: end;
    padding-bottom: 2rem;
`;

/**
 * Function for rendering product box.
 *
 * @param {object} props - the properties object
 * @return {JSX.Element} the rendered product box component
 */

const _ProductBox = (props) => {
    const getExpandImageSrc = () => {
        if (isExpand) {
            return replaceBaseUrl('/evt/assets/brand/ctm/images/icon-minus.png', baseAssetsPath);
        }
        return replaceBaseUrl('/evt/assets/brand/ctm/images/icon-plus.png', baseAssetsPath);
    };
    const [isExpand, setIsExpand] = useState(false);
    const expandImageSrc = useMemo(() => getExpandImageSrc(), [isExpand]);

    const baseAssetsPath = pageContext.getProps().models.pageSchemaModel.getBaseAssetsPath();
    const onPolicyDetailToggleClicked = (event) => {
        event.preventDefault();
        setIsExpand(!isExpand);
    };

    const onConfirmedClicked = () => {
        props.setConfirmFlag(!props.enableCTA);
    };

    const onGetQuoteClicked = (event) => {
      pageContext.getProps().browserHistory.push(`/car-insurance/journey/results`);
    };

    const retrieveList = (collection) => {
        const result = [];
        if (collection) {
            if (collection.includes('<br>')) {
                const collectionArray = collection.split('<br>');
                collectionArray.forEach((element) => {
                    if (element !== '') result.push(element);
                });
            } else if (collection.includes('<li>')) {
                if (typeof document !== 'undefined') {
                    const e = document.createElement('div');
                    e.innerHTML = collection;
                    const list = e.getElementsByTagName('li');

                    list.forEach((element) => {
                        result.push(element.innerText);
                    });
                }
            }
        }

        return result;
    };

    const retrieveExcesses = (productMeta) => {
        const result = [];
        if (productMeta) {
            if (productMeta.excess) {
                result.push({ key: 'Basic Excess', value: `$${productMeta.excess}` });
            }
            if (productMeta.glassExcess) {
                result.push({
                    key: 'Glass Excess',
                    value: `$${productMeta.glassExcess}`
                });
            }
        }

        return result;
    };

    const retrieveAdditionalExcess = (additionalExcess) => {
        const result = [];
        if (additionalExcess) {
            const excessArray = additionalExcess.toArray();
            for (let index = 0; index < excessArray.length; index++) {
                const excess = excessArray[index];
                const o = Object.fromEntries(excess);
                result.push({
                    key: o.description,
                    value: `$${o.amount.replace('$', '')}`
                });
            }
        }
        return result;
    };

    const showDiscountTermsOnClicked = () => {
        props.openDiscountOfferTermsModal();
    };

    const amount = props.amount;
    const isEditable = props.isEditableFlag || false;
    const productMeta = props.productMeta || 'default';
    const providerMeta = props.providerMeta || 'default';
    const discount = !!productMeta && !!productMeta.discount ? Object.fromEntries(productMeta.discount) : 'default';
    const inclusions = retrieveList(props.inclusions) || 'default';
    const benefits = retrieveList(props.benefits) || 'default';
    const optionalExtras = retrieveList(props.optionalExtras) || 'default';
    const specialConditions = retrieveList(props.specialConditions) || 'default';
    const excesses = retrieveExcesses(productMeta) || 'default';
    const disclaimer = retrieveDisclaimer(props.disclaimer) || 'default';
    const underwriter = `Underwriter: ${props.underwriter.name}, ACN ${props.underwriter.acn}, AFS Licence No: ${props.underwriter.afsl}`;
    const additionalExcess = retrieveAdditionalExcess(productMeta.additionalExcesses) || 'default';
    const discountOfferTermsModal = {
        stateProps: {
            isOpen: props.isDiscountOfferTermsModalOpen || false
        },
        eventProps: {
            onClose: [
                {
                    type: 'action',
                    action: {
                        type: 'SET_PAGE_VALUE',
                        identifier: '11MonthsDiscountOfferTermsModalOpen',
                        value: false
                    }
                }
            ],
            allowCloseOnOverlayClick: true
        },
        visualProps: {
            overlayVariant: 'dark',
            outerVariant: 'centered',
            containerVariant: 'centered',
            contentVariant: 'padded'
        }
    };

    const specialConditionReadMoreModal = {
        stateProps: {
            isOpen: props.isSpecialConditionReadMoreModalOpen
        },
        eventProps: {
            onClose: [
                {
                    type: 'action',
                    action: {
                        type: 'SET_PAGE_VALUE',
                        identifier: '11MonthsSpecialConditionReadMoreOpen',
                        value: false
                    }
                }
            ],
            allowCloseOnOverlayClick: true
        },
        visualProps: {
            overlayVariant: 'dark',
            outerVariant: 'centered',
            containerVariant: 'centered',
            contentVariant: 'padded'
        }
    };

    return (
        <Container>
            {isEditable ? (
                <UpdateContainer>
                    <UpdateTitleContainer>
                        <UpdateInfoIconContainer>
                            <FontAwesomeIcon icon={faInfo} color="#FFFFFF" size="xs" />
                        </UpdateInfoIconContainer>
                        <UpdateTitle>Thanks for updating your information</UpdateTitle>
                    </UpdateTitleContainer>
                    <UpdateBodyContainer>
                        <UpdateContentContainer>
                            <UpdateContentHeaderContainer>
                                <UpdateContentHeaderText>We will provide new quotes based on your updated details. Select 'get quotes' below if all the information is now correct.</UpdateContentHeaderText>
                            </UpdateContentHeaderContainer>
                        </UpdateContentContainer>
                    </UpdateBodyContainer>
                </UpdateContainer>
            ) : (
                <>
                    <SummaryContainer>
                        <SummaryContentContainer>
                            <SummaryContentTitleContainer>
                                <SummaryContentTitle>{productMeta?.name ?? 'Gold Comprehensive'}</SummaryContentTitle>
                            </SummaryContentTitleContainer>
                            <SummaryContentHighlightContainer>
                                <SummaryContentHighlightIcon src={replaceBaseUrl('/evt/assets/brand/ctm/images/icon-tick.svg', baseAssetsPath)} alt="tick" />
                                <SummaryContentHighlight>
                                    {discount.discountOffer ?? 'Includes 15% off your first year’s premium for a new Comprehensive Car policy purchased online.'}
                                    {discount.discountOfferTerms && (
                                        <SummaryContentHighlightMoreInfo onClick={showDiscountTermsOnClicked}>&nbsp;More Info</SummaryContentHighlightMoreInfo>
                                    )}
                                </SummaryContentHighlight>
                                {props.isDiscountOfferTermsModalOpen && (
                                    <ModalContainer {...discountOfferTermsModal}>
                                        <DiscountOfferTermsContainer>
                                            <ProviderLogo code={providerMeta?.code ?? 'BUDD'} containerSize="80x48" />
                                            <DiscountOfferTermsHeading>{productMeta.name}</DiscountOfferTermsHeading>
                                        </DiscountOfferTermsContainer>
                                        <TextTemplate innerHTML={discount.discountOfferTerms} size="medium" color="brand_base" />
                                    </ModalContainer>
                                )}
                            </SummaryContentHighlightContainer>
                            <SummaryContentQuoteContainer>
                                <div>
                                    <SummaryContentQuoteExcessLabel>
                      Excess:&nbsp;
                  </SummaryContentQuoteExcessLabel>
                                    <SummaryContentQuoteExcessNumber>
                                        $
                                        {productMeta?.excess ?? 700}
                                    </SummaryContentQuoteExcessNumber>
                                </div>
                                <div>
                                    <SummaryContentQuoteRef>
                                        Quote Ref:
                                        {providerMeta?.quoteNumber ?? 'X4C004591'}
                                    </SummaryContentQuoteRef>
                                </div>
                            </SummaryContentQuoteContainer>
                        </SummaryContentContainer>
                        <ImageContainer>
                            <ProviderLogo code={providerMeta?.code ?? 'BUDD'} containerSize="80x48" />
                            <ImageTCText>T&Cs apply.</ImageTCText>
                            <ImagePrimiumContainer>
                                <PriceDollarSign>$</PriceDollarSign>
                                <PriceContainer>
                                    <PriceWhole>{amount?.dollars.replace(',', '')}</PriceWhole>
                                    <PriceDecimal>{`.${amount?.cents}`}</PriceDecimal>
                                </PriceContainer>
                            </ImagePrimiumContainer>
                            <Frequency>Annually</Frequency>
                        </ImageContainer>
                    </SummaryContainer>
                    <PolicyDetailToggle isExpand={isExpand} onClick={(e) => onPolicyDetailToggleClicked(e)}>
                        <PolicyDetailToggleText>
                            {isExpand ? 'Hide policy details' : 'View policy details'}
                        </PolicyDetailToggleText>
                        <img src={expandImageSrc} alt="plus" />
                    </PolicyDetailToggle>
                    {isExpand && (
                        <>
                            <PolicyDetailFlexBox>
                                <PolicyDetailLeftColumn>
                                    <ProductDetails title="What's included" details={inclusions} showBottom />
                                    <ProductDetails title="Further benefits" details={benefits} showBottom firstItem={false} />
                                    <ProductDetails title="Optional extras and services" details={optionalExtras} showBottom={false} firstItem={false} />
                                </PolicyDetailLeftColumn>
                                <PolicyDetailRightColumn>
                                    <ProductDetails title="Special conditions" details={specialConditions} showBottom readMore={props.specialConditionsReadMore} />
                                    {props.isSpecialConditionReadMoreModalOpen && (
                                        <ModalContainer {...specialConditionReadMoreModal}>
                                            <TextTemplate label={props.specialConditionsReadMore} size="medium" color="brand_base" />
                                        </ModalContainer>
                                    )}
                                    <ExcessDetails title="Excesses" details={excesses} />
                                    <ExcessDetails title="Additional Excesses" details={additionalExcess} />
                                    <ExcessDetails
                                        title="Product disclosure"
                                        details={[
                                            {
                                                key: 'Download the PDS documents below for a full guide on policy limits, inclusions & exclusions'
                                            }
                                        ]}
                                    />
                                    <ProductDisclosureContainer>
                                        <ProductDisclosureText>To view full Product Disclosure Statement</ProductDisclosureText>
                                        <ProductDisclosureLinkContainer href={props.productDisclosure.url} target="_blank">
                                            <ProductDisclosureLinkText>Click here</ProductDisclosureLinkText>
                                            &nbsp;&nbsp;
                                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                                        </ProductDisclosureLinkContainer>
                                    </ProductDisclosureContainer>
                                </PolicyDetailRightColumn>
                            </PolicyDetailFlexBox>
                            <PolicyDetailDisclaimerContainer>
                                <ProductDetails title="Disclaimer" subTitle={disclaimer.title} details={disclaimer.items} paragraph={disclaimer.paragraph} footerText={underwriter} showBottom={false} />
                            </PolicyDetailDisclaimerContainer>
                        </>
                    )}
                </>
            )}
        </Container>
    );
};

_ProductBox.propTypes = {};

/**
 * mapStateToProps - sets up the rules required by this component
 * @param {Object} dispatch - Redux store state.
 * @return {Object} - The state attributes to add to props.
 */

const mapDispatchToProps = (dispatch) => ({
    setConfirmFlag: (newValue) => {
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: '11MonthsEnableCTA',
            value: newValue
        });
    },
    openDiscountOfferTermsModal: () => {
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: '11MonthsDiscountOfferTermsModalOpen',
            value: true
        });
    },
    openSpecialConditionReadMoreModal: () => {
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: '11MonthsSpecialConditionReadMoreOpen',
            value: true
        });
    }
});

const mapStateToProps = (state, item) => {
    const isEditableFlag = state.getIn(['vertical', 'quote', 'form', 'isEditable']) ?? false;
    const hasResults = !!state.getIn(['vertical', 'quote', 'results', 'payload', 'results']);
    const providerMeta = hasResults ? Object.fromEntries(state.getIn(['vertical', 'quote', 'results', 'payload', 'results', 0, 'quotes', 0, 'providerMeta'])) : 'default';
    const productMeta = hasResults ? Object.fromEntries(state.getIn(['vertical', 'quote', 'results', 'payload', 'results', 0, 'quotes', 0, 'productMeta'])) : 'default';
    const enableCTA = state.getIn(['page', '11MonthsEnableCTA']) ?? false;
    const amount = destructNumber(state.getIn(['vertical', 'quote', 'results', 'payload', 'results', 0, 'quotes', 0, 'productMeta', 'price', 'annualPremium']) ?? '1000.00');
    const inclusions = hasResults ? state.getIn(['vertical', 'quote', 'results', 'payload', 'results', 0, 'quotes', 0, 'productMeta', 'inclusions']) : 'default';
    const benefits = hasResults ? state.getIn(['vertical', 'quote', 'results', 'payload', 'results', 0, 'quotes', 0, 'productMeta', 'benefits']) : 'default';
    const optionalExtras = hasResults ? state.getIn(['vertical', 'quote', 'results', 'payload', 'results', 0, 'quotes', 0, 'productMeta', 'optionalExtras']) : 'default';
    const specialConditions = hasResults ? state.getIn(['vertical', 'quote', 'results', 'payload', 'results', 0, 'quotes', 0, 'productMeta', 'specialConditions']) : 'default';
    const specialConditionsReadMore = hasResults ? state.getIn(['vertical', 'quote', 'results', 'payload', 'results', 0, 'quotes', 0, 'productMeta', 'specialConditionsReadMore']) : 'default';
    const disclaimer = hasResults ? state.getIn(['vertical', 'quote', 'results', 'payload', 'results', 0, 'quotes', 0, 'productMeta', 'disclaimer']) : 'default';
    const underwriter = hasResults  ? Object.fromEntries(state.getIn(['vertical', 'quote', 'results', 'payload', 'results', 0, 'quotes', 0, 'productMeta', 'underwriter'])) : 'default';
    const productDisclosure =  hasResults ? Object.fromEntries(state.getIn(['vertical', 'quote', 'results', 'payload', 'results', 0, 'quotes', 0, 'productMeta', 'productDisclosures', 0])) : 'default';
    const isDiscountOfferTermsModalOpen = state.getIn(['page', '11MonthsDiscountOfferTermsModalOpen']) ?? false;
    const isSpecialConditionReadMoreModalOpen = state.getIn(['page', '11MonthsSpecialConditionReadMoreOpen']) ?? false;
    return {
        isEditableFlag,
        providerMeta,
        productMeta,
        amount,
        inclusions,
        benefits,
        optionalExtras,
        specialConditions,
        specialConditionsReadMore,
        disclaimer,
        underwriter,
        productDisclosure,
        enableCTA,
        isDiscountOfferTermsModalOpen,
        isSpecialConditionReadMoreModalOpen
    };
};

const ProductBox = connect(
    mapStateToProps,
    mapDispatchToProps
)(_ProductBox);

export { _ProductBox, ProductBox };
